<template>
  <v-dialog :modelValue="dialog" @click:outside="cerrar" @keydown.esc="cerrar" max-width="1100px" transition="dialog-bottom-transition">
    <v-card>
      <v-form>
        <v-card-title>
          <span class="headline">Aplicar ajustes en {{ code }}</span>
        </v-card-title>

        <v-card-text>
          <!--
            Antes de aplicar se explica el orden, que es lo que sorprende: la ubicación se desbloquea
            PRIMERO y recién después corre el motor de ajuste. Después de aplicar el cartel sobra:
            lo que hay para leer es la tabla.
          -->
          <p v-if="!aplicado" class="text-body-2 mb-4">
            Se desbloquea la ubicación y recién ahí se aplican los ajustes de estas líneas. Las diferencias descartadas y las que todavía no marcaste no se ajustan.
            <b>Los ajustes no se pueden deshacer desde acá.</b>
          </p>

          <v-alert v-if="error_general" type="error" variant="tonal" density="compact" class="mb-4">
            {{ error_general }}
          </v-alert>

          <p v-if="list.length === 0" class="text-body-2 text-grey-600">No hay líneas marcadas para ajustar en esta ubicación.</p>

          <!-- Misma tabla que el detalle de la ubicación: el revisor está mirando las mismas filas -->
          <v-data-table
            v-else
            :headers="headers"
            :items="list"
            item-value="id"
            :items-per-page="-1"
            :hide-default-footer="true"
            class="conteo-lineas tabla-recuadro"
          >
            <template v-slot:item="{ item: linea }">
              <tr>
                <!-- Mismo diseño que Inventario por LPN: nombre arriba, SKU abajo -->
                <td>
                  <div>{{ linea.producto_name }}</div>
                  <span class="text-caption text-grey-600 d-block nowrap">SKU: {{ linea.producto_sku }}</span>
                  <v-chip v-if="linea.is_hallazgo" color="purple" size="x-small" class="mt-1">Hallazgo</v-chip>
                </td>
                <!-- Mismo diseño que Inventario por LPN: el codigo en negrita -->
                <td>
                  <div v-if="linea.lpn">
                    <div class="font-weight-medium">
                      <b>{{ linea.lpn.code }}</b>
                    </div>
                  </div>
                  <span v-else class="text-grey-600">-</span>
                </td>
                <td>
                  <div v-if="linea.atributos && linea.atributos.length > 0">
                    <div v-for="attr in linea.atributos" :key="attr.label" class="atributo-row">
                      <span class="font-weight-bold text-caption">{{ attr.label }}:</span>
                      <span class="text-caption text-grey-600 ml-1">{{ attr.value }}</span>
                    </div>
                  </div>
                  <span v-else class="text-caption text-grey-600">-</span>
                </td>
                <!-- Los dos numeros van juntos: la diferencia solo se entiende comparandolos -->
                <td>
                  <div class="teorico-contado">{{ linea.theoretical_amount }} / {{ linea.counted_amount != null ? linea.counted_amount : "-" }}</div>
                </td>
                <td class="text-right">
                  <span v-if="linea.difference == null" class="text-grey-600">-</span>
                  <span v-else class="font-weight-bold" :class="linea.difference < 0 ? 'text-red' : linea.difference > 0 ? 'text-green' : ''">
                    {{ linea.difference > 0 ? "+" : "" }}{{ linea.difference }}
                  </span>
                </td>
                <!-- Vacío hasta que se aplica: la columna existe desde el principio para que la fila no se mueva -->
                <td>
                  <span v-if="linea.resultado == null" class="text-caption text-grey-600">{{ aplicado ? "Sin respuesta" : "—" }}</span>
                  <span v-else-if="linea.resultado.ok" class="resultado-ok">
                    <v-icon size="small" class="mr-1">mdi-check-circle</v-icon>Ajustada
                  </span>
                  <span v-else class="resultado-error">
                    <v-icon size="small" class="mr-1">mdi-alert-circle</v-icon><span class="text-caption">{{ linea.resultado.msg }}</span>
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>

          <!--
            Un rechazo del motor DESMARCA la línea (StockCountLine::SellarAjuste), así que reintentar
            no es apretar el botón de nuevo: hay que volver a marcarla. Sin este aviso, el segundo
            intento sale "0 líneas ajustadas" y parece que la pantalla no hace nada.
          -->
          <v-alert v-if="hay_fallidas" type="warning" variant="tonal" density="compact" class="mt-4">
            Las líneas que rebotaron volvieron a la cola de revisión y quedaron <b>sin marcar</b>. Para reintentar hay que volver a marcarlas y aplicar de nuevo.
          </v-alert>

          <div v-if="resumen_msg" class="mt-4 text-body-2">{{ resumen_msg }}</div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <!--
            Sin fallas el modal se cierra solo y no se llega a ver esta botonera: queda para el
            caso que la necesita, que es el que hay que leer antes de salir.
          -->
          <template v-if="!aplicado">
            <v-btn color="red darken-1" text @click="cerrar">Cancelar</v-btn>
            <v-btn color="green darken-1" text @click="aplicar" :loading="loading" :disabled="loading || list.length === 0">Aplicar ajustes</v-btn>
          </template>
          <v-btn v-else color="blue-grey" text @click="cerrar">Cerrar</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Aplicación de los ajustes de UNA ubicación, con el resultado línea por línea.
 *
 * Por qué un modal y no un toast: aprobar una ubicación puede aplicar N ajustes y cada uno puede
 * rebotar por su cuenta (un kit que respalda a un padre, stock reservado). El backend devuelve un
 * mensaje con todos los rechazos concatenados, y eso —cinco motivos separados por " | "— no hay
 * forma de leerlo. El detalle por línea sí, y es lo único que le dice al revisor cuál rebotó.
 *
 * La acción vive ACÁ y no en la pantalla, como en ModalReservarStock: el modal muestra lo que se
 * va a hacer, se aplica adentro y el resultado aparece sobre la misma fila que se estaba mirando.
 *
 * Si sale todo bien se cierra solo: la pantalla se recarga y el resultado queda a la vista en la
 * tabla —la ubicación pasa a resuelta y cada línea a "Ajustada"—, así que un cartel encima sería
 * el mismo dato dos veces. Se queda abierto SOLO cuando algo rebotó, que es lo que hay que leer.
 *
 * NO es acumulable: muestra las líneas marcadas AL ABRIRSE. Si después se marcan más, se vuelve
 * a abrir con esas.
 */
export default {
  props: {
    dialog: Boolean,
    //Código de la ubicación, solo para el título
    code: {
      type: String,
      default: "",
    },
    //Las líneas marcadas para ajustar, ya resueltas para mostrar (producto, sku, atributos)
    lineas: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    //Aplica los ajustes y devuelve la promesa con el WSResponse, para poder repartir el detalle
    confirm: Function,
  },
  data: () => ({
    headers: [
      { title: "Producto", key: "producto_name", sortable: false, align: "start" },
      { title: "LPN", key: "lpn", sortable: false, align: "start" },
      { title: "Atributos", key: "atributos", sortable: false, align: "start" },
      { title: "Teórico / Contado", key: "theoretical_amount", sortable: false, align: "center" },
      { title: "Diferencia", key: "difference", sortable: false, align: "end" },
      { title: "Resultado", key: "resultado", sortable: false, align: "start", minWidth: "220px" },
    ],
    //Copia local: las líneas del padre salen de un computed y se rehacen en cada refresh,
    //así que el resultado escrito sobre ellas se perdería
    list: [],
    aplicado: false,
    resumen_msg: "",
    error_general: "",
  }),
  computed: {
    hay_fallidas() {
      return this.list.some((l) => l.resultado != null && !l.resultado.ok);
    },
    /**
     * Éxito completo: se aplicó, el backend no rechazó la ubicación entera y TODAS las líneas
     * volvieron con resultado ok.
     *
     * Una línea sin resultado cuenta como falla a propósito: significa que el backend ajustó otro
     * conjunto del que la pantalla mostraba —alguien desmarcó por otro lado— y eso hay que verlo,
     * no cerrarlo de largo.
     */
    salio_todo_bien() {
      return this.aplicado && this.error_general === "" && this.list.length > 0 && this.list.every((l) => l.resultado != null && l.resultado.ok);
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.list = this.lineas.map((l) => ({ ...l, resultado: null }));
        this.aplicado = false;
        this.resumen_msg = "";
        this.error_general = "";
      }
    },
  },
  methods: {
    /**
     * Lo único que necesita saber la pantalla es si hubo aplicación, para recargar: la ubicación
     * se desbloquea SIEMPRE, aunque todos los ajustes hayan rebotado.
     */
    cerrar() {
      this.$emit("close", this.aplicado);
    },
    aplicar() {
      const promesa = this.confirm();
      if (promesa == null || typeof promesa.then !== "function") return;

      promesa
        .then((response) => {
          this.aplicado = true;

          if (response == null || response.status !== true) {
            this.error_general = response != null && response.msg ? response.msg : "No se pudieron aplicar los ajustes.";
            return;
          }

          const por_id = {};
          const detalle = response.result && response.result.lineas ? response.result.lineas : [];
          detalle.forEach((r) => {
            por_id[r.stock_count_line_id] = r;
          });

          this.list = this.list.map((l) => ({ ...l, resultado: por_id[l.id] != null ? por_id[l.id] : null }));
          this.resumen_msg = response.msg;

          //Sin nada que revisar el modal se va solo, y lo aplicado se lee en la tabla de atrás
          if (this.salio_todo_bien) this.cerrar();
        })
        .catch(() => {
          this.aplicado = true;
          this.error_general = "Ha ocurrido un error al aplicar los ajustes.";
        });
    },
  },
};
</script>

<style scoped>
/*
  Los mismos estilos que la tabla de líneas de Detalle.vue. Allá son scoped y no llegan acá, y
  duplicarlos es preferible a globalizarlos por dos pantallas: si el diseño cambia, cambia en las
  dos y el diff lo muestra.
*/
.tabla-recuadro {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.conteo-lineas :deep(td) {
  padding-top: 8px;
  padding-bottom: 8px;
  vertical-align: middle;
}
.atributo-row {
  line-height: 1.3;
}
/*
  Ancho fijo para que los pares queden en la misma columna visual de una fila a la otra: con el
  ancho por contenido, "1 / 1" y "1200 / 980" no arrancan nunca en el mismo lugar.
*/
.teorico-contado {
  width: 96px;
  margin: 0 auto;
  padding: 4px 8px;
  text-align: center;
  font-weight: bold;
  background: #f5f5f5;
  border-radius: 4px;
}
:deep(.v-chip) {
  font-weight: bold;
}
.nowrap {
  white-space: nowrap;
}
.resultado-ok {
  color: #2e7d32;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
}
.resultado-error {
  color: #c62828;
  font-weight: bold;
  display: inline-flex;
  align-items: flex-start;
}
</style>
